/* @jsx h */
import { defineComponent, ref, onMounted, h } from "vue";
import { RawPayment, PaymentDetails, Order, FoodType } from "@lemmonhq/types";
import { RightSheet, useLocalization } from "@lemmonhq/uikit";
import { SegmentSelect } from "../../components";
import { useAuth } from "../../providers";
import { api } from "@app/data/api";
import { PaymentOverviewComponent } from "./components/PaymentOverviewComponent";
import { PaymentOrdersComponent } from "./components/PaymentOrdersComponent";
import { formatPrice } from "@lemmonhq/orders";

export type PaymentShowProps = {
  rawPayment: RawPayment;
  onClose: Function;
};
export const PaymentShow = defineComponent<PaymentShowProps>({
  name: "PaymentShow",
  props: ["rawPayment", "onClose"],
  setup(props) {
    const { t } = useLocalization();

    const tabs = [
      {
        value: "payment-overview",
        label: t("checkout.payment_show.payment_overview"),
      },
      {
        value: "orders",
        label: t("checkout.payment_show.orders"),
      },
    ];
    const activeTab = ref<string>("payment-overview");
    const auth = useAuth();
    const shop = auth.user.value?.restaurant;

    const payment = ref<PaymentDetails>();
    const orders = ref<Order[]>([]);
    const foodTypes = ref<FoodType[]>([]);
    const loading = ref(true);
    const error = ref<string>();

    onMounted(async () => {
      try {
        loading.value = true;

        // Get payment details
        payment.value = await api.getPayment(props.rawPayment.id);
        foodTypes.value = await api.getFoodTypes();

        // Get order IDs from payment
        const orderIds =
          payment.value.table_orders ??
          (payment.value.order_id ? [payment.value.order_id] : []);

        if (orderIds.length > 0) {
          // Get orders
          orders.value = await api.getOrders(orderIds);
        }
      } catch (err) {
        console.error("Error loading payment details:", err);
        error.value = t("checkout.payment_show.error");
      } finally {
        loading.value = false;
      }
    });

    return () => {
      return (
        <RightSheet
          title={
            <div class="flex flex-1 justify-center items-center pr-3">
              <div class="flex flex-1 flex-col">
                <div class="flex-1 text-2xl text-accent uppercase">
                  {props.rawPayment.method} |{" "}
                  {formatPrice(
                    props.rawPayment.amount + (props.rawPayment.tips || 0),
                    true,
                    2,
                    auth.user.value?.restaurant.currency || "CHF",
                  )}
                </div>
                <div class="text-xs flex gap-2">
                  <div>
                    {" "}
                    {new Date(props.rawPayment.created_at).toLocaleTimeString()}
                  </div>
                  {props.rawPayment.tips ? (
                    <div>
                      <span class="font-semibold">
                        {t("checkout.payment_show.tip_label")}
                      </span>
                      :{" "}
                      {formatPrice(
                        props.rawPayment.tips,
                        true,
                        2,
                        auth.user.value?.restaurant.currency || "CHF",
                      )}
                    </div>
                  ) : null}
                </div>
              </div>
              <div class="flex-1 flex justify-end items-end">
                <SegmentSelect
                  options={tabs}
                  value={activeTab.value}
                  onChange={(v) => {
                    activeTab.value = v;
                  }}
                />
              </div>
            </div>
          }
          isOpen={true}
          onClose={props.onClose}
        >
          <div class="flex flex-col flex-1 overflow-hidden">
            {loading.value ? (
              <div class="flex-1 flex items-center justify-center">
                <span class="loading loading-lg loading-spinner"></span>
              </div>
            ) : error.value ? (
              <div class="flex-1 flex items-center justify-center text-error">
                <p>{error.value}</p>
              </div>
            ) : (
              <div class="flex-1 flex flex-col overflow-y-auto">
                {(function () {
                  if (activeTab.value === "orders" && payment.value) {
                    return (
                      <PaymentOrdersComponent
                        orders={orders.value}
                        foodTypes={foodTypes.value}
                      />
                    );
                  } else if (
                    activeTab.value === "payment-overview" &&
                    payment.value &&
                    shop
                  ) {
                    return (
                      <PaymentOverviewComponent
                        payment={payment.value}
                        shop={shop}
                        orders={orders.value}
                      />
                    );
                  }
                  return null;
                })()}
              </div>
            )}
          </div>
        </RightSheet>
      );
    };
  },
});
